import type { Tournament } from '@airmail/engine';
import { replay, replayLenient } from '@airmail/engine';

/** Backup-Datei (docs/07 §5): Zustand + Log, Version für spätere Migrationen */
export interface BackupFile {
  format: 'airmail-backup';
  version: 1;
  exportedAt: string;
  tournament: Tournament;
}

export function serializeBackup(t: Tournament, now = new Date()): string {
  const file: BackupFile = { format: 'airmail-backup', version: 1, exportedAt: now.toISOString(), tournament: t };
  return JSON.stringify(file, null, 2);
}

export function backupFileName(t: Tournament, now = new Date()): string {
  const slug = t.name
    .toLowerCase()
    .replace(/[^a-z0-9äöüß]+/gi, '-')
    .replace(/^-|-$/g, '');
  const stamp = now.toISOString().slice(0, 16).replace(/[:T]/g, '-');
  return `airmail-${slug || 'turnier'}-${stamp}.json`;
}

export type ParseBackupResult = { ok: true; tournament: Tournament; warnings: string[] } | { ok: false; error: string };

/**
 * Liest ein Backup. Der Zustand wird aus dem Protokoll neu aufgebaut — das
 * Protokoll ist die Wahrheit: Rückgängig, Sync und jedes weitere Replay gehen
 * von ihm aus. Weicht der gespeicherte Snapshot ab, wird trotzdem der neu
 * gebaute Stand genommen und gewarnt; vorher galt der Snapshot, und das
 * nächste Rückgängig oder der nächste Abgleich warf ihn stumm wieder um.
 * Lässt sich das Protokoll nicht streng abspielen, wird tolerant abgespielt:
 * was nicht passt, fällt heraus, und auch das steht in den Warnungen.
 */
export function parseBackup(json: string): ParseBackupResult {
  let raw: unknown;
  try {
    raw = JSON.parse(json);
  } catch {
    return { ok: false, error: 'Datei ist kein gültiges JSON' };
  }
  if (!raw || typeof raw !== 'object' || (raw as BackupFile).format !== 'airmail-backup') return { ok: false, error: 'Keine Airmail-Backup-Datei' };
  const file = raw as BackupFile;
  if (file.version !== 1) return { ok: false, error: `Unbekannte Backup-Version ${String(file.version)}` };
  const t = file.tournament;
  if (!t || typeof t !== 'object' || !t.init || typeof t.init !== 'object' || !Array.isArray(t.log)) return { ok: false, error: 'Turnierdaten fehlen oder sind beschädigt' };
  const warnings: string[] = [];
  let rebuilt: Tournament;
  try {
    rebuilt = replay(t.init, t.log);
  } catch {
    const lenient = replayLenient(t.init, t.log);
    rebuilt = lenient.state;
    warnings.push('Protokoll nicht vollständig abspielbar — nicht passende Aktionen übersprungen');
  }
  if (JSON.stringify(rebuilt) !== JSON.stringify(t)) warnings.push('Snapshot und Protokoll weichen ab — Protokoll verwendet');
  return { ok: true, tournament: rebuilt, warnings };
}
